<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<!-- eslint-disable vue/no-root-v-if -->
<template>
  <PrimeBreadcrumb v-if="path" :model="path" class="breadcrumb-alfresco">
    <template #item="item">
      <div class="breadcrumb-item-wrapper">
        <!-- Clickable link for non-last items or in search/consultation mode -->
        <router-link
          v-if="!isLastItem(item) || isInSearch || isInConsultation"
          :to="item.item.to"
          class="breadcrumb-link"
        >
          <i
            v-if="item.item.nodeType === 'st:site'"
            class="ri-building-line breadcrumb-icon"
            aria-hidden="true"
          />
          <i
            v-else-if="item.item.nodeType === 'home'"
            class="ri-home-4-line breadcrumb-icon"
            aria-hidden="true"
          />
          <i
            v-else-if="item.item.isFolder"
            class="ri-folder-2-line breadcrumb-icon"
            aria-hidden="true"
          />
          <span
            v-if="item.item.name"
            v-tooltip="item.item.name"
            class="breadcrumb-text"
          >
            {{ item.item.name }}
          </span>
        </router-link>

        <!-- Non-clickable span for last item (not in search/consultation) -->
        <span v-else class="breadcrumb-current">
          <i
            v-if="item.item.nodeType === 'st:site'"
            class="ri-building-line breadcrumb-icon"
            aria-hidden="true"
          />
          <i
            v-else-if="item.item.nodeType === 'home'"
            class="ri-home-4-line breadcrumb-icon"
            aria-hidden="true"
          />
          <i
            v-else-if="item.item.isFolder"
            class="ri-folder-2-line breadcrumb-icon"
            aria-hidden="true"
          />
          <span
            v-if="item.item.name"
            v-tooltip="item.item.name"
            class="breadcrumb-text"
          >
            {{ item.item.name }}
          </span>
        </span>

        <PrimeButton
          v-if="isLastItem(item) && !isInSearch && menuActionVisible"
          v-tooltip="$t('moreActions')"
          :aria-label="$t('moreActions')"
          type="button"
          class="breadcrumb-action-btn"
          icon="ri-more-2-fill"
          rounded
          text
          severity="secondary"
          @click="toggleMenuActionButton($event, item)"
        />
      </div>
      <ActionMenu
        ref="breadcrumbMenuAction"
        :actionMenuItems="breadCrumbActions"
      />
    </template>
  </PrimeBreadcrumb>
</template>
<script>
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import { useNavigationStore } from "@/stores/navigation.js";
import { mapState } from "pinia";
import Tooltip from "primevue/tooltip";
import Button from "primevue/button";
import Breadcrumb from "primevue/breadcrumb";
import ActionMenu from "../menu/ActionMenu.vue";

export default {
  name: "BreadCrumbAlfresco",
  components: {
    ActionMenu,
    PrimeButton: Button,
    PrimeBreadcrumb: Breadcrumb,
  },
  directives: {
    tooltip: Tooltip,
  },
  inject: ["getBreadcrumbActions"],
  props: {
    path: {
      type: Array,
      default: () => [],
    },
    isInSearch: {
      type: Boolean,
      default: false,
    },
    menuActionVisible: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["reload"],
  data() {
    return {
      popup: null,
      modifyWorkspaceItem: null,
      workspaceName: null,
      menuActionItems: [],
      breadCrumbNode: {},
      modifyPopupTitle: "",
      workspaceNode: null,
      tempAspectsName: null,
      isSite: false,
      breadCrumbActions: [],
    };
  },
  computed: {
    ...mapState(useNavigationStore, ["isInConsultation", "isInUserHome"]),
  },
  methods: {
    isLastItem(item) {
      return item.item === this.path[this.path.length - 1];
    },
    toggleMenuActionButton(event, item) {
      const target = event.currentTarget || event.target;
      alfrescoNodeService.getNode(item.item.id).then((node) => {
        this.breadCrumbActions = this.getBreadcrumbActions(node);
        this.$refs.breadcrumbMenuAction
          .getActionMenuRef()
          .toggle({ currentTarget: target });
      });
    },
    isNodeIgnore(item, index) {
      return alfrescoFileService.isPathElementVisible(item, index);
    },
  },
};
</script>

<style lang="css" scoped>
.breadcrumb-alfresco {
  background: transparent;
  padding: 0;
  border: none;
}

.breadcrumb-alfresco :deep(.p-breadcrumb-list) {
  flex-wrap: wrap;
  padding: 0;
  margin: 0;
}

.breadcrumb-alfresco :deep(.p-breadcrumb-item) {
  padding: 0;
}

.breadcrumb-alfresco :deep(.p-breadcrumb-separator) {
  padding: 0 0.5rem;
}

.breadcrumb-item-wrapper {
  display: flex;
  align-items: center;
}

.breadcrumb-link,
.breadcrumb-current {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.breadcrumb-link {
  color: var(--p-text-muted-color);
}

.breadcrumb-link:hover {
  color: var(--p-primary-color);
}

.breadcrumb-current {
  color: var(--p-text-color);
  font-weight: 600;
  cursor: default;
}

.breadcrumb-current:hover {
  color: var(--p-text-color);
}

.breadcrumb-icon {
  font-size: 1rem;
}

.breadcrumb-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 11rem;
}

.breadcrumb-action-btn {
  vertical-align: unset !important;
  align-items: center !important;
  height: 1.5rem !important;
  width: 1.5rem !important;
  margin-left: 0.25rem !important;
}
</style>
